// Polls /version.json to detect newly deployed builds, then transparently
// reloads the app with a fresh cache so users always run the latest version
// without needing to manually clear cache or reinstall the PWA.

import { toast } from "sonner";

const STORAGE_KEY = "app_build_version";
const POLL_INTERVAL_MS = 30_000;

let isReloading = false;
let lastChecked = 0;

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.version === "string" ? data.version : null;
  } catch {
    return null;
  }
}

async function purgeCachesAndReload() {
  if (isReloading) return;
  isReloading = true;
  try {
    toast.info("يوجد تحديث جديد — جاري التحديث...", { duration: 1500 });
  } catch {
    // toast may not be ready yet
  }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // ignore
  }
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.update();
        if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    }
  } catch {
    // ignore
  }
  setTimeout(() => {
    window.location.reload();
  }, 800);
}

export async function checkVersionNow(): Promise<void> {
  if (isReloading) return;
  const now = Date.now();
  if (now - lastChecked < 5_000) return;
  lastChecked = now;

  const remote = await fetchRemoteVersion();
  if (!remote) return;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    localStorage.setItem(STORAGE_KEY, remote);
    return;
  }
  if (stored !== remote) {
    localStorage.setItem(STORAGE_KEY, remote);
    void purgeCachesAndReload();
  }
}

export function initVersionCheck() {
  // Kick off after a short delay so initial render finishes first.
  setTimeout(() => void checkVersionNow(), 3_000);

  window.setInterval(() => void checkVersionNow(), POLL_INTERVAL_MS);

  window.addEventListener("focus", () => void checkVersionNow());
  window.addEventListener("online", () => void checkVersionNow());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkVersionNow();
  });
}

// Manual escape hatch — bound to a "Force refresh" button for users stuck on
// an old SW from before this update system shipped.
export async function forceFullRefresh() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // ignore
  }
  localStorage.removeItem(STORAGE_KEY);
  window.location.reload();
}
